import {useCallback, useMemo, useState} from 'react';
import type { SourceEntityRequest} from 'src/api/ProactApiClient';
import {EntityTypeEnum} from 'src/api/ProactApiClient';

const WARNING_THRESHOLD = 0.75;

interface SelectionCounts {
  employees: number;
  managers: number;
  departments: number;
  groups: number;
}

interface UseSelectionWarningProps {
  selectedOptionKey: EntityTypeEnum;
  hasSelectedAll: boolean;
  selectionCounts: SelectionCounts;
  savedValue?: SourceEntityRequest;
}

interface UseSelectionWarningReturn {
  totalCounts: Partial<Record<EntityTypeEnum, number>>;
  handleEmployeeTotalCountChange: (count: number) => void;
  handleManagerTotalCountChange: (count: number) => void;
  handleDepartmentTotalCountChange: (count: number) => void;
  handleGroupTotalCountChange: (count: number) => void;
  selectionWarning: string | null;
  savedSelectionWarning: string | null;
}

export const useSelectionWarning = ({
  selectedOptionKey,
  hasSelectedAll,
  selectionCounts,
  savedValue,
}: UseSelectionWarningProps): UseSelectionWarningReturn => {
  const [totalCounts, setTotalCounts] = useState<
    Partial<Record<EntityTypeEnum, number>>
  >({
    [EntityTypeEnum.Employee]: 0,
    [EntityTypeEnum.Manager]: 0,
    [EntityTypeEnum.Department]: 0,
    [EntityTypeEnum.Group]: 0,
  });

  const handleEmployeeTotalCountChange = useCallback((count: number) => {
    setTotalCounts(prev => ({...prev, [EntityTypeEnum.Employee]: count}));
  }, []);

  const handleManagerTotalCountChange = useCallback((count: number) => {
    setTotalCounts(prev => ({...prev, [EntityTypeEnum.Manager]: count}));
  }, []);

  const handleDepartmentTotalCountChange = useCallback((count: number) => {
    setTotalCounts(prev => ({...prev, [EntityTypeEnum.Department]: count}));
  }, []);

  const handleGroupTotalCountChange = useCallback((count: number) => {
    setTotalCounts(prev => ({...prev, [EntityTypeEnum.Group]: count}));
  }, []);

  const selectionWarning = useMemo(() => {
    const warnings: string[] = [];

    // Check for "Select All" on current tab
    if (hasSelectedAll) {
      const entityTypeName = EntityTypeEnum[selectedOptionKey];
      warnings.push(`all ${entityTypeName}s`);
    }

    // Check each entity type for 75%+ selection
    const entityChecks = [
      {
        type: EntityTypeEnum.Employee,
        selected: selectionCounts.employees,
        total: totalCounts[EntityTypeEnum.Employee] ?? 0,
        name: 'Employees',
      },
      {
        type: EntityTypeEnum.Manager,
        selected: selectionCounts.managers,
        total: totalCounts[EntityTypeEnum.Manager] ?? 0,
        name: 'Managers',
      },
      {
        type: EntityTypeEnum.Department,
        selected: selectionCounts.departments,
        total: totalCounts[EntityTypeEnum.Department] ?? 0,
        name: 'Departments',
      },
      {
        type: EntityTypeEnum.Group,
        selected: selectionCounts.groups,
        total: totalCounts[EntityTypeEnum.Group] ?? 0,
        name: 'Groups',
      },
    ];

    for (const entity of entityChecks) {
      // Skip if this is the "select all" tab (already counted above)
      if (hasSelectedAll && entity.type === selectedOptionKey) {
        continue;
      }
      if (
        entity.total > 0 &&
        entity.selected >= entity.total * WARNING_THRESHOLD
      ) {
        const percentage = Math.round((entity.selected / entity.total) * 100);
        warnings.push(`${percentage}% of ${entity.name}`);
      }
    }

    if (warnings.length === 0) {
      return null;
    }

    const warningList = warnings.join(', ');
    return `You have selected ${warningList}. Are you sure you want to include this many in your selection?`;
  }, [selectedOptionKey, totalCounts, selectionCounts, hasSelectedAll]);

  const savedSelectionWarning = useMemo(() => {
    if (savedValue?.selectAllEntitiesType) {
      const entityTypeName = EntityTypeEnum[savedValue.selectAllEntitiesType];
      return `You have selected all ${entityTypeName}s. This selection includes everyone in this category.`;
    }

    const entityCount = savedValue?.entities?.length ?? 0;
    const totalEmployees = totalCounts[EntityTypeEnum.Employee] ?? 0;
    if (
      totalEmployees > 0 &&
      entityCount >= totalEmployees * WARNING_THRESHOLD &&
      savedValue?.entities?.some(e => e.entityType === EntityTypeEnum.Employee)
    ) {
      return `You have selected a large number of users. Please verify your selections before saving.`;
    }

    return null;
  }, [savedValue?.selectAllEntitiesType, savedValue?.entities, totalCounts]);

  return {
    totalCounts,
    handleEmployeeTotalCountChange,
    handleManagerTotalCountChange,
    handleDepartmentTotalCountChange,
    handleGroupTotalCountChange,
    selectionWarning,
    savedSelectionWarning,
  };
};
